/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { ApolloClient, OneGraphProvider, setContext } from '@oneaudi/onegraph-client';
import { sha256 } from '@oneaudi/onegraph-client/dist/cjs/link/persisted-queries-link';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { AppContent } from '~/app/app-content';
import { AppFeatureServices, AppProvider } from '~/app/contexts/app-context';
import { AsyncStateHolder, State } from '~/app/contexts/reducers/app-reducer';
import { normalizeConfig } from '~/app/FeatureHubAppDefinition';
import { AEM_MARKET_CONFIG, AemMarketConfig } from '~/app/market-config';
import { GlobalStyle } from '~/app/styles/app-style';
import { ENABLE_PERSISTED_QUERIES, GetHttpLink } from '~/utils/apollo';
import { documentAvailable } from '~/utils/client-detection';

interface FeatureAppProps {
	featureServices: AppFeatureServices;
	initialState: AsyncStateHolder;
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	oneGraphClient: ApolloClient<any>;
}

const FeatureApp = ({ featureServices, initialState, oneGraphClient }: FeatureAppProps) => {
	const { loggerService, contentService } = featureServices;

	const getAemConfig = (config: AemMarketConfig) => {
		if (!config) return null;
		const {
			market,
			language,
			carlineId,
			hideStandardAccessories,
			stockCarConfig,
			nbas,
			additionalPrimaryNba,
			combinations,
			summaryFooterCta,
			secondarySummaryCta,
			disablePdfNba,
			omnibus,
			deliveryCostUrl,
			showAccessoriesInclusive,
			showAccessoriesExclusive,
			engineCardKeyfacts,
			showCaESummaryText,
			enableFinancingServices,
			enablePartsRateCalculation,
			hideMonthlyRates,
			enableIncentives,
			specialOfferUrl,
			useIframe,
			enableEngineFilter,
			enable3d,
			globalDisclaimer,
			categoryDisclaimers,
			leanCheckoutEnabled,
		} = config;

		const aemConfig: AemMarketConfig = {
			market: market ?? 'de',
			language: language ?? 'de',
			carlineId: carlineId ?? 'q4etron',
			hideStandardAccessories: hideStandardAccessories ?? undefined,
			disablePdfNba: disablePdfNba ?? false,
			stockCarConfig: stockCarConfig ?? undefined,
			combinations: combinations ?? undefined,
			nbas: nbas ?? undefined,
			additionalPrimaryNba: additionalPrimaryNba ?? undefined,
			summaryFooterCta: summaryFooterCta ?? undefined,
			secondarySummaryCta: secondarySummaryCta ?? undefined,
			omnibus: omnibus ?? undefined,
			deliveryCostUrl: deliveryCostUrl ?? undefined,
			showAccessoriesInclusive: showAccessoriesInclusive ?? undefined,
			showAccessoriesExclusive: showAccessoriesExclusive ?? undefined,
			engineCardKeyfacts: engineCardKeyfacts ?? undefined,
			showCaESummaryText: showCaESummaryText ?? undefined,
			enableFinancingServices: enableFinancingServices ?? undefined,
			hideMonthlyRates: enableFinancingServices ? hideMonthlyRates : enableFinancingServices || undefined,
			enablePartsRateCalculation: enablePartsRateCalculation ?? undefined,
			enableIncentives: enableIncentives ?? undefined,
			specialOfferUrl: specialOfferUrl ?? undefined,
			useIframe: useIframe ?? undefined,
			enableEngineFilter: enableEngineFilter ?? true,
			enable3d: enable3d ?? undefined,
			globalDisclaimer: globalDisclaimer ?? undefined,
			categoryDisclaimers: categoryDisclaimers ?? undefined,
			leanCheckoutEnabled: leanCheckoutEnabled ?? undefined,
		};

		Object.assign(AEM_MARKET_CONFIG, aemConfig);
		return aemConfig;
	};

	const [, setAemConfig] = useState(getAemConfig(normalizeConfig(contentService?.getContent())));

	useEffect(() => {
		const listener = () => {
			const params = new URLSearchParams(window.location.search);
			params.delete('pr');
			/* eslint-disable-next-line no-restricted-globals */
			history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
			setAemConfig(normalizeConfig(contentService?.getContent()));
		};
		contentService?.onContentChange(listener);

		return () => {
			contentService?.removeOnContentChange(listener);
		};
	}, [contentService]);

	const [localState, setState] = useState<State | undefined>(() => {
		if (typeof initialState === 'object') {
			return initialState;
		}
		return undefined;
	});

	useEffect(() => {
		// when asyncStateHolder is a function it means the state could not be properly serialized by the server and it is
		// not available on the client.
		// In that case this effect will try to fetch the state as soon as the component is mounted on the client.
		if (typeof initialState === 'function') {
			loggerService?.info('SSR did not serialize any state');
			initialState().then((freshData) => {
				setState(freshData);
			});
		} else {
			loggerService?.info('SSR serialized state: ', localState);
		}
	}, []);

	// initialize AVP 3d showroom
	useEffect(() => {
		const script = document.createElement('script');

		script.src = 'https://avp-3dws-deck.eu-west-1-web-streaming.avp.tech/avp-3dws-deck.js';
		script.type = 'module';

		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	const LinkHeader = setContext(() => {
		if (documentAvailable()) {
			document.cookie.search(new RegExp(`^jwt_[A-Z]{2}_[a-z]{2}$`));
			const match = document.cookie.match(/jwt_[A-Z]{2}_[a-z]{2}/);
			if (match) {
				const jwt = Cookies.get(match[0]);
				return {
					headers: {
						Authorization: `Bearer ${jwt}`,
						'apollographql-client-name': 'fa-ngc-carconfigurator',
						'x-feature-flags': 'pr7-extensions#on',
						'Cache-Control': 'no-cache',
					},
				};
			}
		}

		return {
			headers: {
				'apollographql-client-name': 'fa-ngc-carconfigurator',
				'x-feature-flags': 'pr7-extensions#on',
			},
		};
	});

	const link = LinkHeader.concat(GetHttpLink());
	oneGraphClient.setLink(
		ENABLE_PERSISTED_QUERIES
			? createPersistedQueryLink({
					sha256,
					useGETForHashedQueries: true,
				}).concat(link)
			: link,
	);

	if (!localState) {
		loggerService?.info('No State found');
		return null;
	}

	return (
		<OneGraphProvider client={oneGraphClient}>
			<AppProvider featureServices={featureServices} initialState={localState}>
				<GlobalStyle />
				<AppContent />
			</AppProvider>
		</OneGraphProvider>
	);
};

export default FeatureApp;
